import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import AdminToast from '../AdminToast';
import AcademiaSelect from '../../common/AcademiaSelect';
import DocumentPreviewModal from '../../common/DocumentPreviewModal';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import { fetchAuthenticatedFileBlob } from '../../../services/api';
import { fetchAdminAmbassadeurDetail } from '../../../services/academiaAmbassadeurAdminApi';
import {
  patchAdminAmbassadeur,
  resendAmbassadeurAccess,
  uploadAdminRegistrationDocument,
} from '../../../services/academiaAdminService';
import { formatMoney } from '../../../utils/currency';
import { AcademicPanelCard, AcademicPanelTableWrap } from './AcademicPanelCard';

const INPUT_CLASS =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

const ZONE_PROMOTION_OPTIONS = [
  'Réseaux sociaux',
  'Campus / universités',
  'Écoles / instituts',
  'Quartier / communauté',
  'Entreprises / collègues',
  'Églises / associations',
  'Événements / terrain',
];

function isImageFile(file: File) {
  return file.type.startsWith('image/');
}

function isPdfFile(file: File) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

const COMMISSION_STATUT_LABELS: Record<string, string> = {
  EN_ATTENTE_SEUIL: 'En attente de seuil',
  CALCULEE: 'Calculée',
  CREDITEE_WALLET: 'Créditée wallet',
  ANNULEE: 'Annulée',
};

type DetailData = {
  utilisateur?: {
    id?: string;
    telephone?: string;
    email?: string | null;
    codeParrainage?: string;
    qrCodePayload?: string | null;
    doitChangerMotDePasse?: boolean;
    dateCreation?: string;
  };
  profil?: { prenom?: string; nom?: string; urlAvatar?: string | null };
  profilAmbassadeur?: {
    zonePromotion?: string | null;
    biographie?: string | null;
    profilComplet?: boolean;
    estActif?: boolean;
  };
  pieceIdentite?: {
    id: string;
    typeDocument?: string;
    statutValidation?: string;
    dateCreation?: string;
  } | null;
  portefeuille?: { solde?: number; devise?: string } | null;
  registerUrl?: string | null;
  synthese?: {
    nombreFilleuls?: number;
    soldeWallet?: number;
    commissionsTotal?: number;
    commissionsCreditees?: number;
    commissionsEnAttente?: number;
    nombreCommissions?: number;
  };
  filleuls?: Array<{
    id: string;
    dateCreation: string;
    codeUtilise: string;
    aPayeFormation?: boolean;
    commissionStatut?: string;
    montantCommissionCdf?: number;
    tauxPct?: number | null;
    filleul: {
      id: string;
      nomComplet: string;
      telephone?: string;
      email?: string | null;
      role?: string;
      matricule?: string | null;
      dateInscription?: string;
    };
  }>;
  commissions?: Array<{
    id: string;
    montantCommission: number;
    montantBase: number;
    tauxPct: number;
    statut: string;
    typeProfil: string;
    dateCreation: string;
  }>;
};

function formatDate(value?: string | null) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR');
}

function DetailGrid({ rows }: { rows: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {rows.map((row) => (
        <div key={row.label} className="rounded-lg border border-outline-variant/15 bg-white/80 px-3 py-2.5">
          <dt className="text-[11px] font-bold uppercase tracking-wide text-on-surface-variant">{row.label}</dt>
          <dd className="mt-1 text-sm font-semibold text-slate-900">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

type FilleulRow = NonNullable<DetailData['filleuls']>[number];
type CommissionRow = NonNullable<DetailData['commissions']>[number];

function FilleulPaiementBadge({
  aPayeFormation,
  commissionStatut,
  montantCommissionCdf,
  tauxPct,
}: {
  aPayeFormation?: boolean;
  commissionStatut?: string;
  montantCommissionCdf?: number;
  tauxPct?: number | null;
}) {
  if (!aPayeFormation) {
    return (
      <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
        Compte créé — pas encore payé
      </span>
    );
  }
  if (commissionStatut === 'CREDITEE_WALLET') {
    return (
      <span
        className="inline-flex flex-col gap-0.5 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800"
        title="Participation intégralement payée — commission déjà créditée au portefeuille du parrain"
      >
        <span>Participation payée · commission versée</span>
        {montantCommissionCdf != null && montantCommissionCdf > 0 ? (
          <span className="font-medium text-emerald-700">
            {formatMoney(montantCommissionCdf, 'CDF')}
            {tauxPct != null ? ` (${tauxPct} %)` : ''}
          </span>
        ) : null}
      </span>
    );
  }
  if (commissionStatut === 'EN_ATTENTE_SEUIL') {
    return (
      <span
        className="inline-flex flex-col gap-0.5 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-900"
        title="Participation payée — commission calculée, en attente du seuil de filleuls payeurs"
      >
        <span>Participation payée · commission en attente de seuil</span>
        {montantCommissionCdf != null && montantCommissionCdf > 0 ? (
          <span className="font-medium">
            {formatMoney(montantCommissionCdf, 'CDF')}
            {tauxPct != null ? ` (${tauxPct} %)` : ''}
          </span>
        ) : null}
      </span>
    );
  }
  if (commissionStatut === 'CALCULEE') {
    return (
      <span className="inline-flex items-center rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800">
        Participation payée · commission calculée
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-emerald-50/80 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
      Participation payée
    </span>
  );
}

function FilleulsTableSection({ items }: { items: FilleulRow[] }) {
  const { paginatedItems, currentPage, totalPages, onPageChange, summary } =
    useAdminTablePagination(items);

  return (
    <AcademicPanelCard
      icon="group"
      title={`Parrainés (${items.length})`}
      variant="preview"
      flush={items.length > 0}
    >
      {items.length === 0 ? (
        <p className="text-sm text-on-surface-variant">Aucun filleul enregistré pour cet ambassadeur.</p>
      ) : (
        <>
          <p className="border-b border-outline-variant/15 px-4 py-2 text-xs text-on-surface-variant">
            Indicateur : participation intégralement payée et commission % liée (une fois par filleul).
          </p>
          <AcademicPanelTableWrap>
            <table className="min-w-full text-sm">
              <thead className="bg-surface-container-low text-left text-xs font-bold uppercase text-on-surface-variant">
                <tr>
                  <th className="px-3 py-2.5">Personne</th>
                  <th className="px-3 py-2.5">Rôle</th>
                  <th className="px-3 py-2.5">Matricule</th>
                  <th className="px-3 py-2.5">Paiement / commission</th>
                  <th className="px-3 py-2.5">Date lien</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((row) => (
                  <tr key={row.id} className="border-t border-outline-variant/15">
                    <td className="px-3 py-2.5">
                      <span className="block font-semibold text-slate-900">{row.filleul.nomComplet}</span>
                      <span className="text-xs text-on-surface-variant">
                        {row.filleul.email ?? row.filleul.telephone}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">{row.filleul.role ?? '—'}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{row.filleul.matricule ?? '—'}</td>
                    <td className="px-3 py-2.5">
                      <FilleulPaiementBadge
                        aPayeFormation={row.aPayeFormation}
                        commissionStatut={row.commissionStatut}
                        montantCommissionCdf={row.montantCommissionCdf}
                        tauxPct={row.tauxPct}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-xs">{formatDate(row.dateCreation)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AcademicPanelTableWrap>
          <AdminPagination
            className="border-t border-outline-variant/15 px-4 py-3"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            summary={summary}
          />
        </>
      )}
    </AcademicPanelCard>
  );
}

function CommissionsTableSection({ items }: { items: CommissionRow[] }) {
  const { paginatedItems, currentPage, totalPages, onPageChange, summary } =
    useAdminTablePagination(items);

  return (
    <AcademicPanelCard
      icon="payments"
      title={`Commissions récentes (${items.length})`}
      variant="success"
      flush={items.length > 0}
    >
      {items.length === 0 ? (
        <p className="text-sm text-on-surface-variant">Aucune commission enregistrée.</p>
      ) : (
        <>
          <AcademicPanelTableWrap>
            <table className="min-w-full text-sm">
              <thead className="bg-surface-container-low text-left text-xs font-bold uppercase text-on-surface-variant">
                <tr>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Montant</th>
                  <th className="px-3 py-2.5">Taux</th>
                  <th className="px-3 py-2.5">Statut</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((c) => (
                  <tr key={c.id} className="border-t border-outline-variant/15">
                    <td className="px-3 py-2.5 text-xs">{formatDate(c.dateCreation)}</td>
                    <td className="px-3 py-2.5 font-semibold">{formatMoney(c.montantCommission, 'CDF')}</td>
                    <td className="px-3 py-2.5">{c.tauxPct} %</td>
                    <td className="px-3 py-2.5">{COMMISSION_STATUT_LABELS[c.statut] ?? c.statut}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AcademicPanelTableWrap>
          <AdminPagination
            className="border-t border-outline-variant/15 px-4 py-3"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            summary={summary}
          />
        </>
      )}
    </AcademicPanelCard>
  );
}

function PieceIdentiteViewer({
  documentId,
  label,
  onOpenFullscreen,
}: {
  documentId: string | null | undefined;
  label: string;
  onOpenFullscreen: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState('');

  useEffect(() => {
    if (!documentId) {
      setBlobUrl(null);
      setContentType('');
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchAuthenticatedFileBlob(
          `/admin/documents/${encodeURIComponent(documentId)}/fichier`,
        );
        if (cancelled) {
          URL.revokeObjectURL(result.blobUrl);
          return;
        }
        createdUrl = result.blobUrl;
        setBlobUrl(result.blobUrl);
        setContentType(result.contentType);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Impossible de charger la pièce.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [documentId]);

  if (!documentId) {
    return (
      <p className="rounded-xl border border-dashed border-outline-variant/30 bg-slate-50 px-4 py-10 text-center text-sm text-on-surface-variant">
        Aucune pièce d&apos;identité déposée pour cet ambassadeur.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">{label}</p>
        <button
          type="button"
          className="academic-panel-btn academic-panel-btn--secondary !px-3 !py-1.5 text-xs"
          onClick={onOpenFullscreen}
        >
          <LucideSymbolIcon name="visibility" tone="inherit" size={14} />
          Agrandir
        </button>
      </div>
      <div className="overflow-hidden rounded-xl border border-outline-variant/20 bg-slate-100/80 p-3">
        {loading ? (
          <p className="py-12 text-center text-sm text-on-surface-variant">Chargement de la pièce…</p>
        ) : error ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-800">{error}</p>
        ) : blobUrl && contentType.startsWith('image/') ? (
          <img
            src={blobUrl}
            alt="Pièce d'identité"
            className="mx-auto max-h-[420px] max-w-full rounded-lg bg-white object-contain shadow-sm"
          />
        ) : blobUrl && (contentType.includes('pdf') || contentType === 'application/pdf') ? (
          <iframe
            title="Pièce d'identité PDF"
            src={blobUrl}
            className="h-[420px] w-full rounded-lg border border-outline-variant/15 bg-white shadow-sm"
          />
        ) : blobUrl ? (
          <div className="py-10 text-center">
            <p className="text-sm text-on-surface-variant">Aperçu intégré non disponible.</p>
            <button
              type="button"
              className="mt-3 text-sm font-bold text-primary hover:underline"
              onClick={onOpenFullscreen}
            >
              Ouvrir le document
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function AcademiaAmbassadeurDetailPanel() {
  const { ambassadeurId } = useParams<{ ambassadeurId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [piecePreviewOpen, setPiecePreviewOpen] = useState(false);
  const [pieceIdentiteFile, setPieceIdentiteFile] = useState<File | null>(null);
  const [pieceLocalPreviewUrl, setPieceLocalPreviewUrl] = useState<string | null>(null);
  const [form, setForm] = useState({
    prenom: '',
    nom: '',
    telephone: '',
    email: '',
    zonePromotion: '',
    biographie: '',
    estActif: true,
  });

  useEffect(() => {
    if (!pieceIdentiteFile) {
      setPieceLocalPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pieceIdentiteFile);
    setPieceLocalPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pieceIdentiteFile]);

  const load = useCallback(async () => {
    if (!ambassadeurId) return;
    setLoading(true);
    setError('');
    try {
      const data = (await fetchAdminAmbassadeurDetail(ambassadeurId)) as DetailData;
      setDetail(data);
      setForm({
        prenom: String(data.profil?.prenom ?? ''),
        nom: String(data.profil?.nom ?? ''),
        telephone: String(data.utilisateur?.telephone ?? ''),
        email: String(data.utilisateur?.email ?? ''),
        zonePromotion: String(data.profilAmbassadeur?.zonePromotion ?? ''),
        biographie: String(data.profilAmbassadeur?.biographie ?? ''),
        estActif: Boolean(data.profilAmbassadeur?.estActif),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger la fiche');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [ambassadeurId]);

  useRegisterPortalRefresh(() => void load());

  useEffect(() => {
    void load();
  }, [load]);

  const nomComplet =
    [detail?.profil?.prenom, detail?.profil?.nom].filter(Boolean).join(' ') ||
    detail?.utilisateur?.telephone ||
    'Ambassadeur';

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!ambassadeurId) return;
    setSaving(true);
    try {
      let pieceIdentiteUrl: string | undefined;
      let uploadToken: string | undefined;
      if (pieceIdentiteFile) {
        const uploaded = await uploadAdminRegistrationDocument(pieceIdentiteFile);
        pieceIdentiteUrl = uploaded.path;
        uploadToken = uploaded.uploadToken ?? undefined;
      }
      const data = (await patchAdminAmbassadeur(ambassadeurId, {
        prenom: form.prenom,
        nom: form.nom,
        telephone: form.telephone,
        email: form.email,
        zonePromotion: form.zonePromotion || null,
        biographie: form.biographie || null,
        estActif: form.estActif,
        ...(pieceIdentiteUrl ? { pieceIdentiteUrl, uploadToken } : {}),
      })) as DetailData;
      setDetail(data);
      setPieceIdentiteFile(null);
      setToast({
        message: pieceIdentiteUrl
          ? 'Informations et pièce d’identité mises à jour.'
          : 'Informations mises à jour.',
        variant: 'success',
      });
      await load();
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Enregistrement impossible',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell
      title={nomComplet}
      description="Fiche ambassadeur — profil, parrainés et commissions."
      isLoading={loading}
      sideCard={{
        label: 'Filleuls',
        value: String(detail?.synthese?.nombreFilleuls ?? 0),
        helper: detail?.utilisateur?.codeParrainage
          ? `Code ${detail.utilisateur.codeParrainage}`
          : 'Parrainage',
        sideLinks: [
          { label: 'Liste ambassadeurs', to: '/admin/ambassadeurs', icon: 'campaign' },
          { label: 'Parrainage', to: '/admin/parrainage', icon: 'share' },
        ],
      }}
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={() => setToast(null)}
      />

      <div className="mb-4">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          onClick={() => navigate('/admin/ambassadeurs')}
        >
          <LucideSymbolIcon name="arrow_back" tone="inherit" size={16} />
          Retour aux ambassadeurs
        </button>
      </div>

      {error ? (
        <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {error}
        </p>
      ) : null}

      {detail ? (
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: 'Wallet',
                value: formatMoney(Number(detail.synthese?.soldeWallet ?? 0), 'CDF'),
              },
              {
                label: 'Filleuls',
                value: String(detail.synthese?.nombreFilleuls ?? 0),
              },
              {
                label: 'Commissions',
                value: formatMoney(Number(detail.synthese?.commissionsTotal ?? 0), 'CDF'),
              },
              {
                label: 'Statut',
                value: !detail.profilAmbassadeur?.estActif
                  ? 'Inactif'
                  : detail.profilAmbassadeur?.profilComplet
                    ? 'Actif'
                    : 'Profil incomplet',
              },
            ].map((card) => (
              <div key={card.label} className="rounded-xl border border-outline-variant/20 bg-white p-3 shadow-sm">
                <p className="text-[11px] font-bold uppercase tracking-wide text-on-surface-variant">
                  {card.label}
                </p>
                <p className="mt-1 text-lg font-black text-slate-900">{card.value}</p>
              </div>
            ))}
          </div>

          <AcademicPanelCard icon="badge" title="Identité & accès" variant="accent">
            <DetailGrid
              rows={[
                { label: 'Code parrainage', value: detail.utilisateur?.codeParrainage ?? '—' },
                {
                  label: 'Lien inscription',
                  value: detail.registerUrl ? (
                    <a
                      href={detail.registerUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all text-primary hover:underline"
                    >
                      {detail.registerUrl}
                    </a>
                  ) : (
                    '—'
                  ),
                },
                { label: 'Créé le', value: formatDate(detail.utilisateur?.dateCreation) },
                {
                  label: 'Mot de passe',
                  value: detail.utilisateur?.doitChangerMotDePasse
                    ? 'Provisoire (à changer)'
                    : 'Défini par l’ambassadeur',
                },
                {
                  label: "Pièce d'identité",
                  value: detail.pieceIdentite?.id ? 'Déposée — voir ci-dessous' : 'Non déposée',
                },
              ]}
            />
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                className="academic-panel-btn academic-panel-btn--secondary"
                onClick={() =>
                  void (async () => {
                    try {
                      const res = await resendAmbassadeurAccess(ambassadeurId!);
                      setToast({
                        message: res.message ?? 'Accès renvoyé par e-mail.',
                        variant: 'success',
                      });
                      await load();
                    } catch (err) {
                      setToast({
                        message: err instanceof Error ? err.message : 'Échec',
                        variant: 'error',
                      });
                    }
                  })()
                }
              >
                <LucideSymbolIcon name="forward_to_inbox" tone="inherit" size={16} />
                Renvoyer accès
              </button>
              <Link
                to="/admin/activites-ambassadeurs"
                className="academic-panel-btn academic-panel-btn--secondary"
              >
                <LucideSymbolIcon name="history" tone="inherit" size={16} />
                Journal d’activités
              </Link>
            </div>
          </AcademicPanelCard>

          <AcademicPanelCard icon="badge" title="Pièce d'identité" variant="preview">
            <PieceIdentiteViewer
              documentId={detail.pieceIdentite?.id}
              label={
                detail.pieceIdentite?.dateCreation
                  ? `Déposée le ${formatDate(detail.pieceIdentite.dateCreation)}`
                  : "Document d'identité"
              }
              onOpenFullscreen={() => setPiecePreviewOpen(true)}
            />
            <DocumentPreviewModal
              open={piecePreviewOpen}
              onClose={() => setPiecePreviewOpen(false)}
              title="Pièce d'identité"
              subtitle={nomComplet}
              apiPath={
                detail.pieceIdentite?.id
                  ? `/admin/documents/${encodeURIComponent(detail.pieceIdentite.id)}/fichier`
                  : null
              }
            />
            <p className="mt-3 text-xs text-on-surface-variant">
              Pour ajouter ou remplacer ce document, utilisez le formulaire « Modifier les informations »
              ci-dessous.
            </p>
          </AcademicPanelCard>

          <AcademicPanelCard icon="edit" title="Modifier les informations" variant="default">
            <form className="space-y-4" onSubmit={handleSave}>
              <div className="grid gap-3 sm:grid-cols-2">
                {(
                  [
                    ['prenom', 'Prénom'],
                    ['nom', 'Nom'],
                    ['telephone', 'Téléphone'],
                    ['email', 'E-mail'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="block text-sm">
                    <span className="text-xs font-bold text-on-surface-variant">{label}</span>
                    <input
                      className={`${INPUT_CLASS} mt-1`}
                      value={form[key]}
                      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                      required={key === 'prenom' || key === 'nom' || key === 'email'}
                    />
                  </label>
                ))}
                <label className="block text-sm sm:col-span-2">
                  <span className="text-xs font-bold text-on-surface-variant">Zone de promotion</span>
                  <AcademiaSelect
                    className="mt-1"
                    value={form.zonePromotion}
                    onChange={(e) => setForm((f) => ({ ...f, zonePromotion: e.target.value }))}
                  >
                    <option value="">Sélectionner une zone</option>
                    {ZONE_PROMOTION_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </AcademiaSelect>
                </label>
                <label className="block text-sm sm:col-span-2">
                  <span className="text-xs font-bold text-on-surface-variant">Biographie</span>
                  <textarea
                    className={`${INPUT_CLASS} mt-1 min-h-[96px] resize-y`}
                    rows={4}
                    value={form.biographie}
                    onChange={(e) => setForm((f) => ({ ...f, biographie: e.target.value }))}
                    placeholder="Présentation, réseaux, approche de promotion…"
                  />
                </label>
                <div className="space-y-3 sm:col-span-2">
                  <label className="block text-sm">
                    <span className="text-xs font-bold text-on-surface-variant">
                      Pièce d&apos;identité{' '}
                      <span className="font-medium normal-case">
                        ({detail.pieceIdentite ? 'remplacer' : 'ajouter'} — image ou PDF)
                      </span>
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.pdf"
                      className={`${INPUT_CLASS} mt-1`}
                      onChange={(e) => setPieceIdentiteFile(e.target.files?.[0] ?? null)}
                    />
                    <span className="mt-1 block text-[11px] text-on-surface-variant">
                      {pieceIdentiteFile
                        ? `Nouveau fichier : ${pieceIdentiteFile.name}`
                        : detail.pieceIdentite
                          ? 'Laissez vide pour conserver la pièce actuelle.'
                          : 'Formats acceptés : JPG, PNG, WebP, GIF, PDF.'}
                    </span>
                  </label>
                  {pieceIdentiteFile && pieceLocalPreviewUrl ? (
                    <div className="rounded-xl border border-dashed border-outline-variant/35 bg-slate-50/80 p-3">
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-on-surface-variant">
                        Aperçu du nouveau fichier
                      </p>
                      {isImageFile(pieceIdentiteFile) ? (
                        <img
                          src={pieceLocalPreviewUrl}
                          alt="Aperçu pièce d'identité"
                          className="mx-auto max-h-64 max-w-full rounded-lg border border-outline-variant/20 bg-white object-contain shadow-sm"
                        />
                      ) : isPdfFile(pieceIdentiteFile) ? (
                        <iframe
                          title="Aperçu PDF pièce d'identité"
                          src={pieceLocalPreviewUrl}
                          className="h-72 w-full rounded-lg border border-outline-variant/20 bg-white shadow-sm"
                        />
                      ) : (
                        <p className="py-6 text-center text-sm text-on-surface-variant">
                          Aperçu non disponible pour ce type de fichier ({pieceIdentiteFile.name}).
                        </p>
                      )}
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          className="text-xs font-semibold text-red-700 hover:underline"
                          onClick={() => setPieceIdentiteFile(null)}
                        >
                          Retirer le fichier
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 sm:col-span-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-outline-variant/40 text-primary"
                    checked={form.estActif}
                    onChange={(e) => setForm((f) => ({ ...f, estActif: e.target.checked }))}
                  />
                  Compte actif
                </label>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="academic-panel-btn academic-panel-btn--primary disabled:opacity-60"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
              </button>
            </form>
          </AcademicPanelCard>

          <FilleulsTableSection items={detail.filleuls ?? []} />

          <CommissionsTableSection items={detail.commissions ?? []} />
        </div>
      ) : null}
    </AdminPageShell>
  );
}
